import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import api from '../lib/api';
import { translateDomain, allDomainsTranslated, toFrenchDomain } from '../translations';

export default function AuditorDashboard() {
  const { user, refreshUser } = useAuth();
  const { lang } = useLang();
  const [activeTab, setActiveTab] = useState('calendar');
  const [selectedDates, setSelectedDates] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [showRefineModal, setShowRefineModal] = useState(false);
  const [selectedBookingForRefine, setSelectedBookingForRefine] = useState(null);
  const [refineCurrentMonth, setRefineCurrentMonth] = useState(new Date());
  const [selectedPhase, setSelectedPhase] = useState('audit');
  const [refineDates, setRefineDates] = useState({
    prep_dates: [],
    travel_before_dates: [],
    audit_dates: [],
    travel_after_dates: [],
    report_dates: []
  });
  const [loadingMessage, setLoadingMessage] = useState(null);
  
  const [profileName, setProfileName] = useState(user?.name || '');
  const [expertiseDomains, setExpertiseDomains] = useState([]);
  const [cvFile, setCvFile] = useState(null);
  const [cvUrl, setCvUrl] = useState(user?.cv_url || null);

  const allDomains = allDomainsTranslated(lang);

  const statusLabels = {
    pending: { fr: 'En attente', en: 'Pending', color: '#f59e0b' },
    accepted: { fr: 'Accepté', en: 'Accepted', color: '#10b981' },
    rejected: { fr: 'Refusé', en: 'Rejected', color: '#ef4444' },
    info_needed: { fr: 'Infos demandées', en: 'Info needed', color: '#3b82f6' },
    planning: { fr: 'Planification', en: 'Planning', color: '#8b5cf6' },
    conduct: { fr: 'En cours', en: 'In progress', color: '#06b6d4' },
    report: { fr: 'Rapport soumis', en: 'Report submitted', color: '#14b8a6' },
    closed: { fr: 'Clôturé', en: 'Closed', color: '#6b7280' }
  };

  const phaseColors = {
    prep: { color: '#fbbf24', label: { fr: 'Préparation', en: 'Preparation' } },
    travel_before: { color: '#3b82f6', label: { fr: 'Voyage aller', en: 'Travel before' } },
    audit: { color: '#10b981', label: { fr: 'Audit', en: 'Audit' } },
    travel_after: { color: '#8b5cf6', label: { fr: 'Voyage retour', en: 'Travel after' } },
    report: { color: '#f97316', label: { fr: 'Rapport', en: 'Report' } }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (activeTab === 'reservations') {
      loadBookings();
    }
  }, [activeTab]);

  const loadProfile = async () => {
    try {
      const response = await api.get("/profile");
      const profile = response.data;
      
      setProfileName(profile.name || "");
      setExpertiseDomains(profile.expertise_domains || []);
      setAvailability(profile.availability || []);
      setBlocked(profile.blocked_dates || []);
      setCvUrl(profile.cv_url || null);
    } catch (error) {
      console.error("❌ Erreur chargement profil:", error);
    }
  };

  const loadBookings = async () => {
    try {
      const response = await api.get('/bookings');
      setBookings(response.data);
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const handleBookingAction = async (bookingId, status, notes = '') => {
    const messages = {
      accepted: { fr: 'Acceptation en cours...', en: 'Accepting...' },
      rejected: { fr: 'Refus en cours...', en: 'Rejecting...' },
      info_needed: { fr: 'Envoi en cours...', en: 'Sending...' }
    };
    
    setLoadingMessage(lang === 'fr' ? messages[status].fr : messages[status].en);
    try {
      await api.put(`/bookings/${bookingId}`, { status, notes });
      toast.success(lang === 'fr' ? 'Réservation mise à jour' : 'Booking updated');
      loadBookings();
      loadProfile();
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur de mise à jour' : 'Update error');
    } finally {
      setLoadingMessage(null);
    }
  };

  const handleRefineDateClick = (dateKey) => {
    const phaseKey = selectedPhase === 'prep' ? 'prep_dates' :
                     selectedPhase === 'travel_before' ? 'travel_before_dates' :
                     selectedPhase === 'audit' ? 'audit_dates' :
                     selectedPhase === 'travel_after' ? 'travel_after_dates' :
                     'report_dates';
    
    setRefineDates(prev => {
      const newDates = {
        prep_dates: prev.prep_dates.filter(d => d !== dateKey),
        travel_before_dates: prev.travel_before_dates.filter(d => d !== dateKey),
        audit_dates: prev.audit_dates.filter(d => d !== dateKey),
        travel_after_dates: prev.travel_after_dates.filter(d => d !== dateKey),
        report_dates: prev.report_dates.filter(d => d !== dateKey)
      };
      
      if (prev[phaseKey].includes(dateKey)) {
        return newDates;
      } else {
        newDates[phaseKey] = [...newDates[phaseKey], dateKey];
        return newDates;
      }
    });
  };

  const getDatePhase = (dateKey) => {
    if (refineDates.prep_dates.includes(dateKey)) return 'prep';
    if (refineDates.travel_before_dates.includes(dateKey)) return 'travel_before';
    if (refineDates.audit_dates.includes(dateKey)) return 'audit';
    if (refineDates.travel_after_dates.includes(dateKey)) return 'travel_after';
    if (refineDates.report_dates.includes(dateKey)) return 'report';
    return null;
  };

  const handleConfirmRefinement = async () => {
    const totalDays = refineDates.prep_dates.length + 
                     refineDates.travel_before_dates.length + 
                     refineDates.audit_dates.length + 
                     refineDates.travel_after_dates.length + 
                     refineDates.report_dates.length;
    
    if (totalDays === 0) {
      toast.error(lang === 'fr' ? 'Sélectionnez au moins un jour' : 'Select at least one day');
      return;
    }
    
    setLoadingMessage(lang === 'fr' ? 'Sauvegarde du planning...' : 'Saving schedule...');
    try {
      await api.post(`/bookings/${selectedBookingForRefine.id}/refine-dates`, refineDates);
      toast.success(lang === 'fr' ? 'Planning affiné !' : 'Schedule refined!');
      setShowRefineModal(false);
      loadBookings();
      loadProfile();
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoadingMessage(null);
    }
  };

  const handleStartAudit = async (bookingId) => {
    if (!window.confirm(lang === 'fr' ? 'Commencer cet audit ?' : 'Start this audit?')) return;
    
    setLoadingMessage(lang === 'fr' ? 'Démarrage de l\'audit...' : 'Starting audit...');
    try {
      await api.post(`/bookings/${bookingId}/start-audit`);
      toast.success(lang === 'fr' ? 'Audit commencé !' : 'Audit started!');
      loadBookings();
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoadingMessage(null);
    }
  };

  const handleUploadDraftReport = async (bookingId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    setLoadingMessage(lang === 'fr' ? 'Envoi du rapport...' : 'Uploading report...');
    try {
      await api.post(`/bookings/${bookingId}/upload-draft-report`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(lang === 'fr' ? 'Rapport envoyé !' : 'Report sent!');
      loadBookings();
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur d\'envoi' : 'Send error');
    } finally {
      setLoadingMessage(null);
    }
  };

  const styles = {
    container: { minHeight: '100vh', backgroundColor: '#f9fafb', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
    content: { maxWidth: '1400px', margin: '0 auto', padding: '32px 24px' },
    title: { fontSize: '32px', fontWeight: '700', color: '#111827', marginBottom: '32px' },
    tabs: { display: 'flex', gap: '8px', marginBottom: '32px', borderBottom: '2px solid #e5e7eb' },
    tab: { padding: '12px 24px', fontSize: '15px', fontWeight: '500', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: '-2px', display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280', transition: 'all 0.2s' },
    tabActive: { color: '#111827', borderBottomColor: '#111827' },
    card: { backgroundColor: '#ffffff', borderRadius: '12px', padding: '32px', border: '1px solid #e5e7eb', marginBottom: '24px' },
    cardTitle: { fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '24px' },
    label: { display: 'block', fontSize: '14px', fontWeight: '500', color: '#111827', marginBottom: '8px' },
    input: { width: '100%', padding: '12px 16px', fontSize: '15px', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '20px', boxSizing: 'border-box' },
    emailText: { fontSize: '15px', color: '#6b7280', marginBottom: '20px' },
    checkboxGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginTop: '16px' },
    checkboxItem: { display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' },
    checkbox: { width: '20px', height: '20px', borderRadius: '4px', border: '2px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', flexShrink: 0 },
    checkboxChecked: { backgroundColor: '#111827', borderColor: '#111827', color: '#ffffff' },
    checkboxLabel: { fontSize: '15px', color: '#374151', userSelect: 'none' },
    uploadButton: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', fontSize: '15px', fontWeight: '500', color: '#374151', backgroundColor: '#ffffff', border: '2px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer' },
    fileInput: { display: 'none' },
    cvLink: { marginTop: '12px', fontSize: '14px', color: '#2563eb', textDecoration: 'none' },
    saveButton: { padding: '14px 32px', fontSize: '16px', fontWeight: '500', color: '#ffffff', backgroundColor: '#111827', border: 'none', borderRadius: '8px', cursor: 'pointer' },
    legend: { display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' },
    legendItem: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#6b7280' },
    legendDot: { width: '16px', height: '16px', borderRadius: '4px' },
    calendarContainer: { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '24px', marginBottom: '24px' },
    calendarGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '48px' },
    monthContainer: { width: '100%' },
    monthHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '0 8px' },
    monthTitle: { fontSize: '18px', fontWeight: '600', color: '#111827', textTransform: 'capitalize' },
    navButton: { width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' },
    weekDays: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' },
    weekDay: { textAlign: 'center', fontSize: '12px', fontWeight: '500', color: '#6b7280', padding: '8px 0' },
    daysGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' },
    day: { aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s', fontWeight: '500', border: '2px solid transparent' },
    dayOtherMonth: { color: '#d1d5db' },
    dayAvailable: { backgroundColor: '#d1fae5', color: '#065f46' },
    dayBlocked: { backgroundColor: '#e5e7eb', color: '#6b7280' },
    daySelected: { backgroundColor: '#111827', color: '#ffffff' },
    buttonGroup: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
    button: { padding: '12px 24px', fontSize: '15px', fontWeight: '500', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' },
    buttonGreen: { backgroundColor: '#10b981', color: '#ffffff' },
    buttonRed: { backgroundColor: '#ffffff', color: '#ef4444', border: '2px solid #ef4444' },
    buttonGray: { backgroundColor: '#ffffff', color: '#6b7280', border: '2px solid #e5e7eb' },
    buttonBlue: { backgroundColor: '#3b82f6', color: '#ffffff' },
    buttonPurple: { backgroundColor: '#8b5cf6', color: '#ffffff' },
    bookingCard: { padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '16px' },
    bookingHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
    bookingTitle: { fontSize: '18px', fontWeight: '600' },
    statusBadge: { padding: '4px 12px', borderRadius: '6px', fontSize: '14px', fontWeight: '500' },
    bookingDetails: { fontSize: '14px', color: '#6b7280', lineHeight: '1.6', marginBottom: '16px' },
    bookingActions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    link: { color: '#2563eb', textDecoration: 'none', fontSize: '14px' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: '#ffffff', borderRadius: '12px', padding: '32px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflowY: 'auto' },
    modalTitle: { fontSize: '24px', fontWeight: '600', marginBottom: '16px' },
    modalButtons: { display: 'flex', gap: '12px', marginTop: '24px' },
    buttonSecondary: { backgroundColor: '#ffffff', color: '#6b7280', border: '2px solid #e5e7eb' },
    buttonPrimary: { backgroundColor: '#111827', color: '#ffffff' },
    phaseSelector: { display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' },
    phaseButton: { padding: '10px 20px', fontSize: '14px', fontWeight: '500', border: '2px solid', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' },
    phaseLegend: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', marginBottom: '16px' },
    phaseLegendItem: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' },
    phaseDot: { width: '12px', height: '12px', borderRadius: '3px' },
    loadingOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 99999 },
    spinner: { width: '60px', height: '60px', border: '6px solid rgba(255, 255, 255, 0.3)', borderTop: '6px solid #ffffff', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    loadingText: { marginTop: '24px', color: '#ffffff', fontSize: '18px', fontWeight: '500' }
  };
const toggleExpertise = (domain) => {
    const frenchDomain = toFrenchDomain(domain);
    
    setExpertiseDomains(prev =>
      prev.includes(frenchDomain) ? prev.filter(d => d !== frenchDomain) : [...prev, frenchDomain]
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCvFile(file);
      handleCvUpload(file);
    }
  };

  const handleCvUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload-cv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCvUrl(response.data.cv_url);
      await refreshUser();
      toast.success(lang === 'fr' ? 'CV téléchargé !' : 'CV uploaded!');
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur upload CV' : 'CV upload error');
    }
  };

  const handleDeleteCv = async () => {
    if (!window.confirm(lang === 'fr' ? 'Supprimer le CV ?' : 'Delete CV?')) return;
    
    try {
      await api.delete('/upload-cv');
      setCvUrl(null);
      await refreshUser();
      toast.success(lang === 'fr' ? 'CV supprimé !' : 'CV deleted!');
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur suppression CV' : 'CV deletion error');
    }
  };

  const handleSaveProfile = async () => {
    try {
      await api.put('/profile', {
        name: profileName,
        expertise_domains: expertiseDomains
      });
      await refreshUser();
      toast.success(lang === 'fr' ? 'Profil sauvegardé !' : 'Profile saved!');
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur sauvegarde' : 'Save error');
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    
    const days = [];
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({ day: prevMonthLastDay - i, isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true });
    }
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ day: i, isCurrentMonth: false });
    }
    return days;
  };

  const formatDateKey = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const toggleDateSelection = (year, month, day) => {
    const dateKey = formatDateKey(year, month, day);
    setSelectedDates(prev => 
      prev.includes(dateKey) ? prev.filter(d => d !== dateKey) : [...prev, dateKey]
    );
  };

  const markAvailable = async () => {
    const newAvailability = [...new Set([...availability, ...selectedDates])];
    const newBlocked = blocked.filter(d => !selectedDates.includes(d));
    
    setAvailability(newAvailability);
    setBlocked(newBlocked);
    setSelectedDates([]);

    try {
      await api.put('/profile', {
        availability: newAvailability,
        blocked_dates: newBlocked
      });
      await refreshUser();
      toast.success(lang === 'fr' ? 'Disponibilités sauvegardées !' : 'Availability saved!');
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur sauvegarde' : 'Save error');
    }
  };

  const markBlocked = async () => {
    const newBlocked = [...new Set([...blocked, ...selectedDates])];
    const newAvailability = availability.filter(d => !selectedDates.includes(d));
    
    setBlocked(newBlocked);
    setAvailability(newAvailability);
    setSelectedDates([]);

    try {
      await api.put('/profile', {
        availability: newAvailability,
        blocked_dates: newBlocked
      });
      await refreshUser();
      toast.success(lang === 'fr' ? 'Blocages sauvegardés !' : 'Blocks saved!');
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur sauvegarde' : 'Save error');
    }
  };

  const clearSelection = () => {
    setSelectedDates([]);
  };

  const renderMonth = (monthOffset) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1);
    const days = getDaysInMonth(date);
    const monthName = date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { month: 'long', year: 'numeric' });

    return (
      <div style={styles.monthContainer}>
        <div style={styles.monthHeader}>
          {monthOffset === 0 && (
            <button 
              style={styles.navButton}
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
            >
              ‹
            </button>
          )}
          <div style={styles.monthTitle}>{monthName}</div>
          {monthOffset === 1 && (
            <button 
              style={styles.navButton}
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
            >
              ›
            </button>
          )}
        </div>

        <div style={styles.weekDays}>
          {['lu', 'ma', 'me', 'je', 've', 'sa', 'di'].map(day => (
            <div key={day} style={styles.weekDay}>{day}</div>
          ))}
        </div>

        <div style={styles.daysGrid}>
          {days.map((dayObj, idx) => {
            const dateKey = formatDateKey(date.getFullYear(), date.getMonth(), dayObj.day);
            const isSelected = selectedDates.includes(dateKey);
            const isAvailable = availability.includes(dateKey);
            const isBlocked = blocked.includes(dateKey);

            let dayStyle = { ...styles.day };
            if (!dayObj.isCurrentMonth) {
              dayStyle = { ...dayStyle, ...styles.dayOtherMonth };
            } else if (isSelected) {
              dayStyle = { ...dayStyle, ...styles.daySelected };
            } else if (isAvailable) {
              dayStyle = { ...dayStyle, ...styles.dayAvailable };
            } else if (isBlocked) {
              dayStyle = { ...dayStyle, ...styles.dayBlocked };
            }

            return (
              <div
                key={idx}
                style={dayStyle}
                onClick={() => dayObj.isCurrentMonth && toggleDateSelection(date.getFullYear(), date.getMonth(), dayObj.day)}
              >
                {dayObj.day}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderRefineCalendar = () => {
    const date = refineCurrentMonth;
    const days = getDaysInMonth(date);
    const monthName = date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { month: 'long', year: 'numeric' });

    return (
      <>
        <div style={styles.monthHeader}>
          <button 
            style={styles.navButton}
            onClick={() => setRefineCurrentMonth(new Date(refineCurrentMonth.getFullYear(), refineCurrentMonth.getMonth() - 1, 1))}
          >
            ‹
          </button>
          <div style={styles.monthTitle}>{monthName}</div>
          <button 
            style={styles.navButton}
            onClick={() => setRefineCurrentMonth(new Date(refineCurrentMonth.getFullYear(), refineCurrentMonth.getMonth() + 1, 1))}
          >
            ›
          </button>
        </div>

        <div style={styles.weekDays}>
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
            <div key={day} style={styles.weekDay}>{day}</div>
          ))}
        </div>

        <div style={styles.daysGrid}>
          {days.map((dayObj, idx) => {
            const dateKey = formatDateKey(date.getFullYear(), date.getMonth(), dayObj.day);
            const phase = getDatePhase(dateKey);

            let dayStyle = { ...styles.day };
            if (!dayObj.isCurrentMonth) {
              dayStyle = { ...dayStyle, ...styles.dayOtherMonth };
            } else if (phase) {
              dayStyle = { 
                ...dayStyle, 
                backgroundColor: phaseColors[phase].color, 
                color: '#ffffff',
                border: '2px solid ' + phaseColors[phase].color
              };
            }

            return (
              <div
                key={idx}
                style={dayStyle}
                onClick={() => dayObj.isCurrentMonth && handleRefineDateClick(dateKey)}
              >
                {dayObj.day}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div style={styles.container}>
      <Navbar />
      
      {/* LOADING OVERLAY - BLOQUE TOUTE LA PAGE */}
      {loadingMessage && (
        <div style={styles.loadingOverlay}>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>{loadingMessage}</div>
        </div>
      )}
      
      <div style={styles.content}>
        <h1 style={styles.title}>
          {lang === 'fr' ? 'Tableau de bord Auditeur' : 'Auditor Dashboard'}
        </h1>

        <div style={styles.tabs}>
          <button style={{ ...styles.tab, ...(activeTab === 'calendar' ? styles.tabActive : {}) }} onClick={() => setActiveTab('calendar')}>
            📅 {lang === 'fr' ? 'Calendrier' : 'Calendar'}
          </button>
          <button style={{ ...styles.tab, ...(activeTab === 'reservations' ? styles.tabActive : {}) }} onClick={() => setActiveTab('reservations')}>
            {lang === 'fr' ? 'Réservations' : 'Reservations'}
          </button>
          <button style={{ ...styles.tab, ...(activeTab === 'profile' ? styles.tabActive : {}) }} onClick={() => setActiveTab('profile')}>
            {lang === 'fr' ? 'Profil' : 'Profile'}
          </button>
        </div>

        {activeTab === 'calendar' && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>📅 {lang === 'fr' ? 'Mon calendrier' : 'My calendar'}</div>

            <div style={styles.legend}>
              <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#d1fae5' }}></div><span>{lang === 'fr' ? 'Disponible' : 'Available'}</span></div>
              <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#e5e7eb' }}></div><span>{lang === 'fr' ? 'Bloqué' : 'Blocked'}</span></div>
              <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#bfdbfe' }}></div><span>{lang === 'fr' ? 'Réservé' : 'Reserved'}</span></div>
              <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#111827' }}></div><span>{lang === 'fr' ? 'Sélectionné' : 'Selected'}</span></div>
            </div>

            <div style={styles.calendarContainer}>
              <div style={styles.calendarGrid}>
                {renderMonth(0)}
                {renderMonth(1)}
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button style={{ ...styles.button, ...styles.buttonGreen }} onClick={markAvailable} disabled={selectedDates.length === 0}>
                ✓ {lang === 'fr' ? 'Marquer disponible' : 'Mark available'}
              </button>
              <button style={{ ...styles.button, ...styles.buttonRed }} onClick={markBlocked} disabled={selectedDates.length === 0}>
                ✗ {lang === 'fr' ? 'Bloquer' : 'Block'}
              </button>
              <button style={{ ...styles.button, ...styles.buttonGray }} onClick={clearSelection} disabled={selectedDates.length === 0}>
                {lang === 'fr' ? 'Supprimer sélection' : 'Clear selection'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'reservations' && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>{lang === 'fr' ? 'Mes réservations' : 'My bookings'}</div>
            {bookings.length === 0 ? (
              <p style={{ color: '#6b7280' }}>{lang === 'fr' ? 'Aucune réservation' : 'No bookings'}</p>
            ) : (
              bookings.map(booking => {
                const status = statusLabels[booking.status] || statusLabels.pending;
                
                return (
                  <div key={booking.id} style={styles.bookingCard}>
                    <div style={styles.bookingHeader}>
                      <div style={styles.bookingTitle}>{booking.sponsor_name}</div>
                      <div style={{ ...styles.statusBadge, backgroundColor: status.color + '20', color: status.color }}>
                        {lang === 'fr' ? status.fr : status.en}
                      </div>
                    </div>
                    <div style={styles.bookingDetails}>
                      <div>📅 {booking.start_date} → {booking.end_date}</div>
                      <div>🏷️ {translateDomain(booking.domain, lang)}</div>
                      {booking.notes && <div>📝 {booking.notes}</div>}
                      {booking.audit_plan_url && (
                        <div>
                          📄 <a href={booking.audit_plan_url} target="_blank" rel="noopener noreferrer" style={styles.link}>
                            {lang === 'fr' ? 'Plan d\'audit' : 'Audit plan'}
                          </a>
                        </div>
                      )}
                      {booking.draft_report_url && (
                        <div>
                          📊 <a href={booking.draft_report_url} target="_blank" rel="noopener noreferrer" style={styles.link}>
                            {lang === 'fr' ? 'Rapport d\'audit' : 'Audit report'}
                          </a>
                        </div>
                      )}
                      {booking.audit_schedule && (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#8b5cf6' }}>
                          ⏱️ {booking.audit_schedule.total_blocked_dates} {lang === 'fr' ? 'jours bloqués' : 'days blocked'}
                        </div>
                      )}
                    </div>
                    
                    <div style={styles.bookingActions}>
                      {booking.status === 'pending' && (
                        <>
                          <button 
                            style={{ ...styles.button, ...styles.buttonGreen }} 
                            onClick={() => handleBookingAction(booking.id, 'accepted')}
                          >
                            ✓ {lang === 'fr' ? 'Accepter' : 'Accept'}
                          </button>
                          <button 
                            style={{ ...styles.button, ...styles.buttonRed }} 
                            onClick={() => handleBookingAction(booking.id, 'rejected')}
                          >
                            ✗ {lang === 'fr' ? 'Refuser' : 'Reject'}
                          </button>
                          <button 
                            style={{ ...styles.button, ...styles.buttonBlue }} 
                            onClick={() => {
                              const notes = prompt(lang === 'fr' ? 'Informations demandées:' : 'Info needed:');
                              if (notes) handleBookingAction(booking.id, 'info_needed', notes);
                            }}
                          >
                            ℹ️ {lang === 'fr' ? 'Plus d\'infos' : 'More info'}
                          </button>
                        </>
                      )}
                      
                      {booking.status === 'planning' && (
                        <>
                          <button 
                            style={{ ...styles.button, ...styles.buttonPurple }} 
                            onClick={() => {
                              setSelectedBookingForRefine(booking);
                              setRefineCurrentMonth(new Date(booking.start_date));
                              setRefineDates({
                                prep_dates: booking.audit_schedule?.prep_dates || [],
                                travel_before_dates: booking.audit_schedule?.travel_before_dates || [],
                                audit_dates: booking.audit_schedule?.audit_dates || [],
                                travel_after_dates: booking.audit_schedule?.travel_after_dates || [],
                                report_dates: booking.audit_schedule?.report_dates || []
                              });
                              setShowRefineModal(true);
                            }}
                          >
                            📅 {lang === 'fr' ? 'Affiner le planning' : 'Refine schedule'}
                          </button>
                          <button 
                            style={{ ...styles.button, ...styles.buttonPurple }} 
                            onClick={() => handleStartAudit(booking.id)}
                          >
                            ▶️ {lang === 'fr' ? 'Commencer l\'audit' : 'Start audit'}
                          </button>
                        </>
                      )}
                      
                      {booking.status === 'conduct' && (
                        <>
                          <input
                            type="file"
                            id={`draft-report-${booking.id}`}
                            style={styles.fileInput}
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => e.target.files[0] && handleUploadDraftReport(booking.id, e.target.files[0])}
                          />
                          <label 
                            htmlFor={`draft-report-${booking.id}`} 
                            style={{ ...styles.uploadButton, backgroundColor: '#14b8a6', color: '#ffffff', cursor: 'pointer' }}
                          >
                            📤 {lang === 'fr' ? 'Envoyer rapport' : 'Send report'}
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <>
            <div style={styles.card}>
              <div style={styles.cardTitle}>{lang === 'fr' ? 'Informations personnelles' : 'Personal Information'}</div>
              <label style={styles.label}>{lang === 'fr' ? 'Nom' : 'Name'}</label>
              <input type="text" style={styles.input} value={profileName} onChange={(e) => setProfileName(e.target.value)} />
              <label style={styles.label}>Email</label>
              <div style={styles.emailText}>{user?.email}</div>
            </div>

            <div style={styles.card}>
              <div style={styles.cardTitle}>{lang === 'fr' ? 'Domaines d\'expertise' : 'Expertise Domains'}</div>
              <div style={styles.checkboxGrid}>
                {allDomains.map((domain) => {
                  const frenchDomain = toFrenchDomain(domain);
                  const isChecked = expertiseDomains.includes(frenchDomain);
                  
                  return (
                    <div key={domain} style={styles.checkboxItem} onClick={() => toggleExpertise(domain)}>
                      <div style={{ ...styles.checkbox, ...(isChecked ? styles.checkboxChecked : {}) }}>
                        {isChecked && '✓'}
                      </div>
                      <span style={styles.checkboxLabel}>{domain}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.cardTitle}>📄 CV</div>
              <input type="file" id="cv-upload" style={styles.fileInput} accept=".pdf,.doc,.docx" onChange={handleFileChange} />
              <label htmlFor="cv-upload" style={styles.uploadButton}>
                ⬆️ {lang === 'fr' ? 'Telecharger un CV' : 'Upload CV'}
              </label>

              {cvUrl && (
                <div style={{ marginTop: '12px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <a href={cvUrl} target="_blank" rel="noopener noreferrer" style={styles.cvLink}>
                    📄 {lang === 'fr' ? 'Voir le CV' : 'View CV'}
                  </a>
                  <button onClick={handleDeleteCv} style={{ ...styles.uploadButton, color: '#ef4444', borderColor: '#ef4444' }}>
                    🗑️ {lang === 'fr' ? 'Supprimer' : 'Delete'}
                  </button>
                </div>
              )}
            </div>

            <button style={styles.saveButton} onClick={handleSaveProfile}>
              {lang === 'fr' ? 'Sauvegarder le profil' : 'Save profile'}
            </button>
          </>
        )}
      </div>

      {showRefineModal && selectedBookingForRefine && (
        <div style={styles.modal} onClick={() => setShowRefineModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalTitle}>
              {lang === 'fr' ? 'Planifier l\'audit sur le calendrier' : 'Schedule audit on calendar'}
            </div>
            
            <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
              {lang === 'fr' 
                ? 'Sélectionnez une phase puis cliquez sur les jours du calendrier'
                : 'Select a phase then click on calendar days'}
            </p>

            <div style={styles.phaseSelector}>
              {Object.entries(phaseColors).map(([key, { color, label }]) => (
                <button
                  key={key}
                  style={{
                    ...styles.phaseButton,
                    backgroundColor: selectedPhase === key ? color : '#ffffff',
                    borderColor: color,
                    color: selectedPhase === key ? '#ffffff' : color
                  }}
                  onClick={() => setSelectedPhase(key)}
                >
                  {lang === 'fr' ? label.fr : label.en}
                </button>
              ))}
            </div>

            <div style={styles.phaseLegend}>
              {Object.entries(phaseColors).map(([key, { color, label }]) => {
                const count = refineDates[`${key}_dates`]?.length || 0;
                return (
                  <div key={key} style={styles.phaseLegendItem}>
                    <div style={{ ...styles.phaseDot, backgroundColor: color }}></div>
                    <span>{lang === 'fr' ? label.fr : label.en}: {count}j</span>
                  </div>
                );
              })}
            </div>

            <div style={styles.calendarContainer}>
              {renderRefineCalendar()}
            </div>

            <div style={{ 
              padding: '16px', 
              backgroundColor: '#f3f4f6', 
              borderRadius: '8px',
              fontSize: '14px',
              color: '#374151',
              marginTop: '16px'
            }}>
              <strong>{lang === 'fr' ? 'Total :' : 'Total:'}</strong> {
                refineDates.prep_dates.length + 
                refineDates.travel_before_dates.length + 
                refineDates.audit_dates.length + 
                refineDates.travel_after_dates.length + 
                refineDates.report_dates.length
              } {lang === 'fr' ? 'jours' : 'days'}
            </div>

            <div style={styles.modalButtons}>
              <button 
                style={{ ...styles.button, ...styles.buttonSecondary }} 
                onClick={() => setShowRefineModal(false)}
              >
                {lang === 'fr' ? 'Annuler' : 'Cancel'}
              </button>
              <button 
                style={{ ...styles.button, ...styles.buttonPrimary }} 
                onClick={handleConfirmRefinement}
              >
                {lang === 'fr' ? 'Confirmer le planning' : 'Confirm schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
